import { useState } from 'react';
import { TextField, FormControl, InputLabel, Select, MenuItem, InputAdornment, Autocomplete, CircularProgress } from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import type { FormFieldConfig } from '@lingaai/types/graph/nodes';
import { GooglePlacesAutocomplete } from './GooglePlacesAutocomplete';
import { RichTextEditor } from './RichTextEditor';
import { useUsers, useNodes } from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';

interface FormFieldProps {
  field: FormFieldConfig;
  value: unknown;
  onChange: (value: unknown) => void;
  picklistOptions?: Array<{ id: string; label: string }>;
}

function toTitleCase(str: string): string {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatNumberWithCommas(value: unknown): string {
  if (value === undefined || value === null || value === '') return '';
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (isNaN(num)) return String(value);
  return num.toLocaleString('en-US');
}

function parseFormattedNumber(str: string): number | '' {
  const cleaned = str.replace(/,/g, '');
  if (cleaned === '') return '';
  const num = parseFloat(cleaned);
  return isNaN(num) ? '' : num;
}

export function FormField({ field, value, onChange, picklistOptions }: FormFieldProps) {
  const stringValue = value !== undefined && value !== null ? String(value) : '';
  const { data: currentUser } = useCurrentUser();
  const { data: users, isLoading: usersLoading } = useUsers(currentUser?.tenant_id || undefined);

  const [lookupSearch, setLookupSearch] = useState('');
  const { data: lookupResults, isLoading: lookupLoading } = useNodes(field.type === 'lookup' && field.lookupConfig ? field.lookupConfig.nodeType : '', {
    search: lookupSearch || undefined,
    pageSize: 20,
    sortBy: 'props.name',
    sortOrder: 'asc',
  });

  switch (field.type) {
    case 'lookup':
      if (!field.lookupConfig) return null;

      const lookupOptions = lookupResults?.data || [];
      const selectedOption = lookupOptions.find((opt) => opt.id === value) || null;

      return (
        <Autocomplete
          options={lookupOptions}
          value={selectedOption}
          onChange={(_event, newValue) => onChange(newValue?.id || '')}
          onInputChange={(_event, newInputValue, reason) => {
            if (reason === 'input') {
              setLookupSearch(newInputValue);
            }
          }}
          getOptionLabel={(option) => (option.props?.name as string) || (option.props?.title as string) || (option.props?.subject as string) || 'Unknown'}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          loading={lookupLoading}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {(option.props?.name as string) || (option.props?.title as string) || (option.props?.subject as string) || 'Unknown'}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              size="small"
              required={field.required}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {lookupLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      );

    case 'select':
      const options = picklistOptions || field.options?.map((o) => ({ id: o, label: toTitleCase(o) })) || [];
      return (
        <FormControl fullWidth size="small" required={field.required}>
          <InputLabel>{field.label}</InputLabel>
          <Select value={stringValue} label={field.label} onChange={(e) => onChange(e.target.value)}>
            {options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    case 'textarea':
      return <TextField label={field.label} value={stringValue} onChange={(e) => onChange(e.target.value)} fullWidth size="small" required={field.required} multiline rows={3} />;
    case 'richtext':
      return <RichTextEditor value={stringValue} onChange={(val) => onChange(val)} placeholder={field.label} />;
    case 'number':
      return (
        <TextField
          label={field.label}
          value={formatNumberWithCommas(value)}
          onChange={(e) => onChange(parseFormattedNumber(e.target.value))}
          fullWidth
          size="small"
          required={field.required}
          inputProps={{ inputMode: 'numeric' }}
          InputProps={
            field.format === 'currency'
              ? {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }
              : undefined
          }
        />
      );
    case 'date':
      return (
        <DatePicker
          label={field.label}
          value={stringValue ? dayjs(stringValue) : null}
          onChange={(newValue) => onChange(newValue?.format('YYYY-MM-DD') || '')}
          slotProps={{ textField: { size: 'small', fullWidth: true, required: field.required } }}
        />
      );
    case 'datetime':
      return (
        <DateTimePicker
          label={field.label}
          value={stringValue ? dayjs(stringValue) : null}
          onChange={(newValue) => onChange(newValue?.toISOString() || '')}
          slotProps={{ textField: { size: 'small', fullWidth: true, required: field.required } }}
        />
      );
    case 'geo': {
      const geoValue = (value as { street?: string; city?: string; state?: string; postal_code?: string; country?: string; latitude?: number; longitude?: number }) || {};
      return <GooglePlacesAutocomplete label={field.label} value={geoValue} onChange={onChange} required={field.required} />;
    }
    case 'user':
      const userOptions =
        users?.map((u) => ({
          id: u.id,
          label: u.full_name || u.email || 'Unknown User',
        })) || [];
      return (
        <FormControl fullWidth size="small" required={field.required} disabled={usersLoading}>
          <InputLabel>{field.label}</InputLabel>
          <Select value={stringValue} label={field.label} onChange={(e) => onChange(e.target.value)}>
            {userOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    default:
      return (
        <TextField
          label={field.label}
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          size="small"
          required={field.required}
          type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : field.type === 'tel' ? 'tel' : 'text'}
        />
      );
  }
}
